import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ChangeEvent,
} from "react";
import { STEP_FIELD_DEBOUNCE_MS } from "./constants";

export function useDebouncedSyncedString({
  syncKey,
  externalValue,
  onCommit,
  debounceMs = STEP_FIELD_DEBOUNCE_MS,
}: {
  syncKey: string;
  externalValue: string;
  onCommit: (params: { value: string }) => void;
  debounceMs?: number;
}) {
  const [draft, setDraft] = useState(externalValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFocusedRef = useRef(false);
  const draftRef = useRef(draft);
  const onCommitRef = useRef(onCommit);
  draftRef.current = draft;
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (isFocusedRef.current) return;
    setDraft(externalValue);
  }, [syncKey, externalValue]);

  const flushCommit = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onCommitRef.current({ value: draftRef.current });
  }, []);

  const scheduleCommit = useCallback(
    (value: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        onCommitRef.current({ value });
      }, debounceMs);
    },
    [debounceMs]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setDraft(v);
      scheduleCommit(v);
    },
    [scheduleCommit]
  );

  const onBlur = useCallback(() => {
    isFocusedRef.current = false;
    flushCommit();
  }, [flushCommit]);

  const onFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onCommitRef.current({ value: draftRef.current });
    };
  }, [syncKey]);

  return { draft, onChange, onBlur, onFocus };
}
